import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef
} from 'react';
import styled from 'styled-components';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import {
  Heading,
  Button,
  TextField,
  ButtonGroup
} from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import {
  NavHeader,
  Loader,
  getTranslatedText,
  EventEmitter,
  Tag,
  CustomModal,
  AccessControl
} from '@nile/components';
import { InvoiceBuilderContextProvider } from '@nile/client_management/src/Contexts/InvoiceBuilderContext';
import { GET_SERVICE_CATEGORIES, AccountClient, useQuery } from '@nile/graphql';
import { cloneDeep, debounce, omit } from 'lodash';
import { Tooltip } from 'react-clone-tippy';
import ServiceCatalogTable from './ServiceCatalogTable';
import { OFFERING_TYPES } from '@nile/banyan/src/Pages/Constants/offeringType';
import { CreateServiceCatalogItem } from '@nile/banyan/src/Pages/ServiceCatalogNew/CreateServiceCatalogItem';
import { withModalWrapper } from '../ChildGroup/ProvisionRuleModal';

const NavHead = styled.div`
  font-size: 12px;
  font-weight: 700;
  padding: 8px 2px;
  padding-top: 0;
  border-bottom: 1px solid #dfdfdf;
  margin: 0 8px;
  margin-bottom: 8px;
  margin-top: 10px;
  a {
    display: block;
  }
`;

const SelectionItem = styled.div<{ selected: boolean }>`
  padding: 10px;
  border-radius: 5px;
  cursor: pointer;
  background: ${(props) =>
    props.selected ? '#F5F5FC !important' : 'transparent'};
  font-weight: ${(props) => (props.selected ? '600' : 'normal')};
`;

function ServiceCatalogModalBase(props) {
  const {
    closeModal,
    onRecordsChange,
    open,
    onSelectionQuery,
    itemsToBeExcluded,
    accessor,
    shouldFilterServiceAndProduct,
    isFromTicketOrProject = false
  } = props;
  const intl = useIntl();

  const { loading, data, refetch } = useQuery(GET_SERVICE_CATEGORIES, {
    client: AccountClient,
    fetchPolicy: 'no-cache'
  });

  const [loadingSave, setLoadingSave] = useState(false);

  const [selectedSection, setSelectedSection] = useState('all');
  const [filterText, setFilterText] = useState(null);
  const [records, setRecords] = useState(props.records);
  const tableRef = useRef<any>(null);

  const setSelectedRecords = (recordsInput, overrideSelectedSection) => {
    const allRecordsSet = [...new Set(recordsInput)];
    const selectedSectionItem =
      overrideSelectedSection || selectedSection || 'all';
    setRecords({
      ...records,
      [selectedSectionItem]: allRecordsSet
    });
  };

  const setRecordsForMultipleSections = (properties) => {
    setRecords({
      ...records,
      ...properties
    });
  };

  const fetchFilteredData = (text) => {
    EventEmitter.dispatch('filterData', text);
  };

  const debouncedFilter = useMemo(() => debounce(fetchFilteredData, 300), []);

  if (loading) return <Loader />;

  const allCategories = data.getServiceCategories
    ? data.getServiceCategories.slice()
    : [];

  const handleSelection = async () => {
    const newRecords: Array<any> = Object.values(records).reduce(
      (acc: [], curr: any) => {
        acc = acc.concat(curr);
        return acc;
      },
      []
    );

    if (newRecords.length) {
      // const records = newRecords.map((el) => {
      //   return el;
      // });

      try {
        setLoadingSave(true);
        await onRecordsChange(newRecords);
      } finally {
        setLoadingSave(false);
        closeModal();
      }
    }
  };

  const contextValue = {
    records: records,
    setSelectedRecords: setSelectedRecords,
    setRecordsForMultipleSections: setRecordsForMultipleSections,
    selectedSection: selectedSection
  };

  let allSelectedItems = [];
  Object.keys(records).forEach((el) => {
    allSelectedItems = allSelectedItems.concat(records[el] || []);
  });

  const checkRowHighlight = (item) => {
    return (
      allSelectedItems?.findIndex(
        (el) => el.itemId === item?.original?.itemId
      ) > -1
    );
  };

  const checkDisabledRow = (item) => {
    return false;
  };

  return (
    <CustomModal open={open} onClose={closeModal}>
      <div className="w1200 vh90 bg-white rounded-2xl ">
        <InvoiceBuilderContextProvider value={contextValue}>
          <div className="flex center p2040">
            <Heading>
              <FormattedMessage id="invoiceBuilder.itemSelection.modal.header" />
            </Heading>
            <div className="w200 ml-20">
              <TextField
                value={filterText}
                placeholder={getTranslatedText(
                  intl,
                  'invoiceBuilder.catalogItems.search.placeholder'
                )}
                onChange={(val) => {
                  setFilterText(val);
                  // setting this to null as it's just a gimmick to focus all categories. Else API call will go for all instead of filtered text.
                  setSelectedSection(null);
                  debouncedFilter(val);
                }}
                autoComplete={false}
                prefix={<SVGIcon name="Search" />}
                suffix={
                  filterText ? (
                    <span
                      className="position-relative z21"
                      onClick={() => {
                        setFilterText(null);
                        debouncedFilter('');
                      }}
                    >
                      <SVGIcon name="CircleCancelMinor" />
                    </span>
                  ) : null
                }
                size="small"
                variant="outline"
              />
            </div>
            <div className="mlauto flex center">
              {allSelectedItems?.length > 0 && (
                <Tooltip
                  distance={5}
                  html={
                    <div className="p15 w300 max-height-500 scroll bshadow bg-white">
                      {allSelectedItems.map((el) => {
                        return (
                          <div className="flex center py8 fsize12 white-space-wrap">
                            <div className="self-start">
                              <SVGIcon
                                name={
                                  OFFERING_TYPES[el?.itemType]?.icon ||
                                  OFFERING_TYPES[
                                    el?.serviceTypeItem?.offeringType
                                  ]?.icon
                                }
                              />
                            </div>
                            <div className="ml8 self-start">
                              {el.name || el.serviceItem?.name}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  }
                  position="bottom-end"
                  inertia
                  trigger="mouseenter"
                  animation="shift"
                  interactive
                  animateFill={false}
                  theme="super-light"
                >
                  <Tag
                    className="ml8 mr8 pointer border-black"
                    background="#FFFEF0"
                  >
                    {`${allSelectedItems.length} ${getTranslatedText(
                      intl,
                      'admin.serviceCatalog.list.selected'
                    )}`}
                  </Tag>
                </Tooltip>
              )}
              <div className="mr-10">
                <CreateServiceCatalogItem
                  isSecondary
                  onSuccess={async (item) => {
                    await refetch();
                    const selectedSectionItem = item.category?.name || 'all';
                    const newRecords =
                      cloneDeep(records[selectedSectionItem]) || [];
                    newRecords.push(item);
                    setSelectedSection(selectedSectionItem);
                    setSelectedRecords(newRecords, selectedSectionItem);
                    if (onSelectionQuery) {
                      const listInfo = tableRef.current?.getListInfo?.(
                        null,
                        selectedSectionItem
                      );
                      await tableRef.current?.fetchViewData?.(
                        null,
                        listInfo,
                        true
                      );
                    }
                    return true;
                  }}
                  isFromTicketOrProject={isFromTicketOrProject}
                  allowOnlyServiceQuantityAndProduct
                />
              </div>
              <ButtonGroup>
                <Button
                  primary
                  icon={<SVGIcon name="Save" />}
                  onClick={() => {
                    handleSelection();
                  }}
                  loading={loadingSave}
                >
                  <FormattedMessage id="common.save" />
                </Button>
              </ButtonGroup>
            </div>
          </div>
          <div className="flex center ch-85">
            <div className="w300 fhp p1025 border-divider-top border-right-gray scroll">
              <NavHead className="fx">
                <NavHeader>
                  <FormattedMessage id="invoiceBuilder.itemSelection.modal.serviceCategories" />
                </NavHeader>
              </NavHead>
              <div className="LinkGroup mb40">
                <SelectionItem
                  className="fx actr ticketing-actions"
                  strict
                  key="category-all"
                  replace
                  selected={selectedSection === 'all' || !selectedSection}
                  onClick={() => {
                    setSelectedSection('all');
                    setFilterText(null);
                  }}
                >
                  <FormattedMessage id="invoiceBuilder.itemSelection.allCategories" />
                </SelectionItem>
                {allCategories.map((el) => {
                  return (
                    <SelectionItem
                      className="fx actr ticketing-actions"
                      strict
                      key={`category-${el.name}`}
                      replace
                      selected={el.name === selectedSection}
                      onClick={() => {
                        setSelectedSection(el.name);
                        setFilterText(null);
                      }}
                    >
                      {el.label || el.name}
                    </SelectionItem>
                  );
                })}
              </div>
            </div>
            <div className="fhp cw-305">
              <ServiceCatalogTable
                ref={tableRef}
                selectedSection={selectedSection}
                setRecords={setSelectedRecords}
                checkDisabledRow={checkDisabledRow}
                checkRowHighlight={checkRowHighlight}
                onSelectionQuery={onSelectionQuery}
                accessor={accessor}
                itemsToBeExcluded={itemsToBeExcluded}
                shouldFilterServiceAndProduct={shouldFilterServiceAndProduct}
              />
            </div>
          </div>
        </InvoiceBuilderContextProvider>
      </div>
    </CustomModal>
  );
}

export const ServiceCatalogModal = withModalWrapper(
  function ServiceCatalogModal({ serviceItems, ...props }) {
    const records =
      serviceItems?.reduce((acc, curr) => {
        // TODO: Remove 'all'
        if (!acc[curr.category?.name ?? 'all']) {
          acc[curr.category?.name ?? 'all'] = [curr];
          return acc;
        }

        acc[curr.category?.name ?? 'all'].push(curr);
        return acc;
      }, {}) ?? {};

    return <ServiceCatalogModalBase records={records} {...props} />;
  }
);
